import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Trophy, Crown, Sparkles, ArrowLeft, Music4, Video, Vote, Check, Info } from "lucide-react";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";

/**
 * /awards/2027 — the "2027 Top Global Music Artist" & "Top Global Creator" awards page.
 *
 * How it works (product spec):
 *  - Every unique (buyer, product) purchase counts as ONE vote for that product's artist.
 *  - Same buyer + same product NEVER counts twice (they get free unlimited downloads).
 *  - Music-category purchases → 2027 Top Global Music Artist standings.
 *  - Video-category purchases → 2027 Top Global Creator standings.
 *  - A separate community poll (1 vote per account) picks the "Cross-Platform Top 100"
 *    from a curated list of world-charting names.
 *  - The community poll leaderboard refreshes live; a weekly snapshot is displayed.
 */

const Podium = ({ leaderboard, title, icon: Icon, testid, emptyCopy }) => (
  <div className="card p-6 sm:p-8" data-testid={testid}>
    <div className="flex items-start gap-3 mb-6">
      <div className="w-11 h-11 rounded-xl bg-[#EC008C]/10 border border-[#EC008C]/25 flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-[#EC008C]" />
      </div>
      <div>
        <h3 className="font-['Unbounded'] text-lg sm:text-xl font-bold text-white leading-tight">{title}</h3>
        <p className="text-xs text-[#8A92A0] mt-1">Live standings · updated on every purchase</p>
      </div>
    </div>

    {!leaderboard || leaderboard.length === 0 ? (
      <div className="text-sm text-[#8A92A0] py-8 text-center">
        {emptyCopy || "No purchases yet — be the first fan to cast a vote."}
      </div>
    ) : (
      <ol className="space-y-2">
        {leaderboard.slice(0, 10).map((row) => (
          <li key={row.artist_id} className="flex items-center gap-3 p-3 rounded-xl bg-[#050810] border border-[#1A2030] hover:border-[#EC008C]/40 transition-colors" data-testid={`award-row-${row.rank}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-['Unbounded'] font-black text-sm flex-shrink-0 ${row.rank === 1 ? "bg-[#FFD34E] text-black" : row.rank <= 3 ? "bg-[#EC008C]/15 text-[#EC008C]" : "bg-[#1A2030] text-white"}`}>
              {row.rank}
            </div>
            <div className="w-10 h-10 rounded-full bg-[#1A2030] overflow-hidden flex-shrink-0">
              {row.profile_image ? <img src={row.profile_image} alt="" className="w-full h-full object-cover" /> : <Music4 className="w-full h-full p-2 text-[#8A92A0]" />}
            </div>
            <div className="flex-1 min-w-0">
              <Link to={`/artist/${row.artist_id}`} className="text-sm text-white truncate hover:text-[#EC008C] transition-colors font-medium">{row.stage_name}</Link>
              {row.genre && <p className="text-[10px] text-[#8A92A0] uppercase tracking-widest">{row.genre}</p>}
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-[#EC008C] font-bold text-sm">{row.votes.toLocaleString()}</p>
              <p className="text-[9px] text-[#8A92A0] uppercase tracking-widest">votes</p>
            </div>
          </li>
        ))}
      </ol>
    )}
  </div>
);

const TopWinnerBanner = ({ leader, awardName, testid }) => (
  <div className="rounded-2xl bg-gradient-to-br from-[#FFD34E]/[0.10] via-[#0A0E18] to-[#EC008C]/[0.06] border border-[#FFD34E]/30 p-6 sm:p-8" data-testid={testid}>
    <div className="flex items-center gap-2 mb-3">
      <Crown className="w-5 h-5 text-[#FFD34E]" />
      <span className="text-[10px] uppercase tracking-widest text-[#FFD34E] font-bold">Current #1</span>
    </div>
    <h2 className="font-['Unbounded'] text-xl sm:text-2xl font-bold text-white mb-2">{awardName}</h2>
    {leader ? (
      <>
        <div className="mt-4 flex items-center gap-4">
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-[#1A2030] overflow-hidden flex-shrink-0 border-2 border-[#FFD34E]/40">
            {leader.profile_image ? <img src={leader.profile_image} alt={leader.stage_name} className="w-full h-full object-cover" /> : <Music4 className="w-full h-full p-4 text-[#8A92A0]" />}
          </div>
          <div>
            <Link to={`/artist/${leader.artist_id}`} className="font-['Unbounded'] text-2xl sm:text-3xl font-black text-white hover:text-[#EC008C] transition-colors block leading-tight">{leader.stage_name}</Link>
            <p className="text-sm text-[#8A92A0]">{leader.genre || "—"} {leader.location ? `· ${leader.location}` : ""}</p>
          </div>
        </div>
        <div className="mt-5 pt-4 border-t border-[#FFD34E]/20 flex items-baseline gap-2">
          <span className="text-3xl sm:text-4xl font-['Unbounded'] font-black text-[#EC008C]">{leader.votes.toLocaleString()}</span>
          <span className="text-[#8A92A0] text-sm">votes so far · ${leader.revenue.toLocaleString()} in fan support</span>
        </div>
      </>
    ) : (
      <p className="text-sm text-[#8A92A0] mt-6">
        No purchases yet. The first artist to sell a track through StardomFans.com automatically holds the crown.
      </p>
    )}
  </div>
);

// -------- Cross-Platform Poll (1 vote per account) --------

const CrossPlatformPoll = ({ token }) => {
  const [candidates, setCandidates] = useState([]);
  const [myVote, setMyVote] = useState(null);
  const [q, setQ] = useState("");
  const [voting, setVoting] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const cs = await axios.get(`${API}/global-poll/candidates`);
      setCandidates(cs.data.candidates || []);
      if (token) {
        try {
          const mv = await axios.get(`${API}/global-poll/my-vote`, { headers: { Authorization: `Bearer ${token}` } });
          setMyVote(mv.data?.artist_key || null);
        } catch { /* ignore */ }
      }
    } finally { setLoading(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const cs = await axios.get(`${API}/global-poll/candidates`);
        setCandidates(cs.data.candidates || []);
        if (token) {
          try {
            const mv = await axios.get(`${API}/global-poll/my-vote`, { headers: { Authorization: `Bearer ${token}` } });
            setMyVote(mv.data?.artist_key || null);
          } catch { /* ignore */ }
        }
      } finally { setLoading(false); }
    })();
  }, [token]);

  const castVote = async (artistKey, name) => {
    if (!token) { toast.error("Sign in to cast your vote"); return; }
    setVoting(artistKey);
    try {
      await axios.post(`${API}/global-poll/vote`, { artist_key: artistKey }, { headers: { Authorization: `Bearer ${token}` } });
      setMyVote(artistKey);
      toast.success(`Your vote is with ${name}. You can switch anytime.`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Vote failed");
    } finally { setVoting(null); }
  };

  const filtered = candidates.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()));
  const topSort = [...filtered];

  return (
    <div className="card p-6 sm:p-8" data-testid="cross-platform-poll">
      <div className="flex items-start gap-3 mb-2">
        <div className="w-11 h-11 rounded-xl bg-[#00E68A]/10 border border-[#00E68A]/25 flex items-center justify-center flex-shrink-0">
          <Vote className="w-5 h-5 text-[#00E68A]" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-['Unbounded'] text-lg sm:text-xl font-bold text-white leading-tight">
            2027 Cross-Platform Top 100
          </h3>
          <p className="text-xs text-[#8A92A0] mt-1">
            One vote per account · Switch anytime · Weekly leaderboard snapshot every Monday.
          </p>
        </div>
      </div>

      <div className="mt-3 p-3 rounded-lg bg-[#00E68A]/[0.06] border border-[#00E68A]/20 flex items-start gap-2 mb-5" data-testid="poll-rules">
        <Info className="w-3.5 h-3.5 text-[#00E68A] flex-shrink-0 mt-0.5" />
        <p className="text-xs text-[#8A92A0]">
          Vote for your favourite globally-charting artist from this curated list of well-known names.
          Each StardomFans account gets exactly one live vote — change it whenever you like. Snapshots
          of the leaderboard freeze every Monday to declare a &ldquo;Weekly #1.&rdquo;
        </p>
      </div>

      <input
        type="text"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={`Search ${candidates.length} candidates…`}
        className="w-full bg-[#050810] border border-[#1A2030] rounded-lg px-3 py-2 text-sm text-white placeholder-[#6A7280] focus:outline-none focus:border-[#EC008C]/50 mb-4"
        data-testid="poll-search"
      />

      {loading ? (
        <div className="text-sm text-[#8A92A0] py-8 text-center">Loading candidates…</div>
      ) : (
        <ul className="max-h-[560px] overflow-y-auto divide-y divide-[#1A2030]/60" data-testid="poll-candidate-list">
          {topSort.map((c) => {
            const mine = myVote === c.artist_key;
            return (
              <li key={c.artist_key} className="flex items-center gap-3 py-2.5" data-testid={`poll-candidate-${c.artist_key}`}>
                <div className="w-6 h-6 rounded-full bg-[#1A2030] flex items-center justify-center flex-shrink-0">
                  <Music4 className="w-3 h-3 text-[#8A92A0]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">{c.name}</p>
                  <p className="text-[10px] text-[#8A92A0]">{c.votes.toLocaleString()} vote{c.votes === 1 ? "" : "s"}</p>
                </div>
                <button
                  onClick={() => castVote(c.artist_key, c.name)}
                  disabled={voting === c.artist_key}
                  className={`text-xs px-3 h-8 rounded-full font-semibold transition-all ${mine ? "bg-[#00E68A]/15 border border-[#00E68A]/40 text-[#00E68A]" : "bg-[#0A0E18] border border-[#1A2030] text-[#8A92A0] hover:border-[#EC008C]/40 hover:text-white"}`}
                  data-testid={`poll-vote-btn-${c.artist_key}`}
                >
                  {mine ? (<span className="flex items-center gap-1"><Check className="w-3 h-3" /> My vote</span>) : (voting === c.artist_key ? "…" : "Vote")}
                </button>
              </li>
            );
          })}
          {topSort.length === 0 && <li className="text-sm text-[#8A92A0] py-8 text-center">No match. Try a different name.</li>}
        </ul>
      )}
    </div>
  );
};

const Awards2027 = () => {
  const { token } = useAuth();
  const [musicLeader, setMusicLeader] = useState(null);
  const [creatorLeader, setCreatorLeader] = useState(null);
  const [musicBoard, setMusicBoard] = useState([]);
  const [creatorBoard, setCreatorBoard] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [ml, cl, mb, cb] = await Promise.all([
          axios.get(`${API}/awards/2027/music-artist`),
          axios.get(`${API}/awards/2027/creator`),
          axios.get(`${API}/awards/2027/top-100?vote_type=music`),
          axios.get(`${API}/awards/2027/top-100?vote_type=creator`),
        ]);
        setMusicLeader(ml.data.leader);
        setCreatorLeader(cl.data.leader);
        setMusicBoard(mb.data.leaderboard || []);
        setCreatorBoard(cb.data.leaderboard || []);
      } catch (e) { /* ignore */ }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#050810]" data-testid="awards-2027-page">
      <header className="fixed top-0 left-0 right-0 z-50 glass-strong">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2">
            <img src="/logo.png" alt="StardomFans" className="w-10 h-10 object-contain" style={{ mixBlendMode: "lighten" }} />
          </Link>
          <Link to="/home">
            <Button variant="ghost" className="text-[#8A92A0] hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" /> Home
            </Button>
          </Link>
        </div>
      </header>

      <main className="pt-32 pb-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FFD34E]/10 border border-[#FFD34E]/25 mb-4">
              <Trophy className="w-3.5 h-3.5 text-[#FFD34E]" />
              <span className="text-[10px] uppercase tracking-widest font-bold text-[#FFD34E]">2027 · Live standings</span>
            </span>
            <h1 className="font-['Unbounded'] text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
              The <span className="text-[#EC008C]">2027 StardomFans Awards</span>
            </h1>
            <p className="text-[#8A92A0] max-w-3xl mx-auto text-sm sm:text-base leading-relaxed">
              Every 30-second preview purchase counts as one vote for the artist. Every 30-or-80-second video-teaser purchase counts as one vote for the creator.
              <strong className="text-white"> Same buyer + same product only counts once — free unlimited redownloads for you, one authentic vote for the artist.</strong>
              To add more votes, buy different tracks / products from the same artist. Authentic counts, always.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-6 mb-12">
            <TopWinnerBanner leader={musicLeader} awardName="StardomFans.com 2027 Top Global Music Artist" testid="banner-music" />
            <TopWinnerBanner leader={creatorLeader} awardName="StardomFans.com 2027 Top Global Creator" testid="banner-creator" />
          </div>

          <div className="grid lg:grid-cols-2 gap-6 mb-12">
            <Podium
              leaderboard={musicBoard}
              title="Top 100 · Music Artists"
              icon={Music4}
              testid="podium-music"
              emptyCopy="No music-track purchases yet — will you cast the first vote?"
            />
            <Podium
              leaderboard={creatorBoard}
              title="Top 100 · Video Creators"
              icon={Video}
              testid="podium-creator"
              emptyCopy="No video-teaser purchases yet — will you cast the first vote?"
            />
          </div>

          <CrossPlatformPoll token={token} />

          <div className="text-center mt-10">
            <Link to="/discover">
              <Button className="bg-[#EC008C] text-black hover:bg-[#FF3399] rounded-full px-8 h-12 font-bold" data-testid="awards-cta">
                <Sparkles className="w-4 h-4 mr-2" /> Browse tracks & cast your vote
              </Button>
            </Link>
            <p className="text-xs text-[#6A7280] mt-4 max-w-xl mx-auto">
              How votes are counted: every distinct product you buy from an artist adds one vote to their standing.
              Same product, same buyer? One vote — but unlimited free redownloads forever.
            </p>
          </div>
        </div>
      </main>

      <footer className="py-12 px-6 border-t border-[#1A2030]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <img src="/logo.png" alt="SF" className="w-10 h-10 object-contain" style={{ mixBlendMode: "lighten" }} />
          <div className="flex gap-6 flex-wrap justify-center">
            <Link to="/terms" className="text-[#8A92A0] hover:text-[#EC008C] text-sm">Terms</Link>
            <Link to="/privacy" className="text-[#8A92A0] hover:text-[#EC008C] text-sm">Privacy</Link>
            <Link to="/security" className="text-[#8A92A0] hover:text-[#EC008C] text-sm">Security</Link>
            <Link to="/privacy-report" className="text-[#00E68A] text-sm font-semibold">Privacy Report</Link>
          </div>
          <p className="text-[#8A92A0] text-sm">&copy; {new Date().getFullYear()} StardomFans Ltd.</p>
        </div>
      </footer>
    </div>
  );
};

export default Awards2027;
